// ---------------------------------------------------------------------------
// MarketFilters.tsx — shared filter bar used by SimulationView.
//
// Changes from previous version:
//  - Added includeTraderScores to MarketFilterValues and DEFAULT_FILTERS.
//  - filtersToParams passes includeTraderScores through to the API.
//  - Rendered as a Checkbox control in the filter bar (mirrors the screener).
// ---------------------------------------------------------------------------

import { useMemo } from 'react';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    TextField,
    ToggleButton,
    Tooltip,
    Checkbox,
    Chip,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import {
    VALID_POLYMARKET_TAGS,
    VALID_EVENT_CATEGORIES,
    CONVICTION_METRICS,
    DEFAULT_METRIC,
    type ConvictionMetric,
    type ConvictionScreenerParams,
    type MetricDef,
} from '../api/useConvictionScreener';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MarketFilterValues {
    group: string;
    sortBy: ConvictionMetric;
    polymarketTags: string[];
    eventCategories: string[];
    minCapitalText: string;
    minTradersText: string;
    hasSignal: boolean;
    includeTraderScores: boolean;   // ← NEW: wired to API param
    limit: number;
    minPriceText: string;           // entry price lower bound (0–1), optional
    maxPriceText: string;           // entry price upper bound (0–1), optional
    maxAgeDaysText: string;         // max age in days (maps to maxDaysRunning)
    minEdgeDisparityText: string;   // min edge disparity (0.0+)
}

export const DEFAULT_FILTERS: MarketFilterValues = {
    group: 'insider',
    sortBy: DEFAULT_METRIC,
    polymarketTags: [],
    eventCategories: [],
    minCapitalText: '',
    minTradersText: '',
    hasSignal: false,
    includeTraderScores: false,
    limit: 200,
    minPriceText: '',
    maxPriceText: '',
    maxAgeDaysText: '7',
    minEdgeDisparityText: '0.0',
};

// ---------------------------------------------------------------------------
// filtersToParams — maps UI state → ConvictionScreenerParams
// ---------------------------------------------------------------------------

export function filtersToParams(f: MarketFilterValues): ConvictionScreenerParams {
    return {
        group: f.group,
        sortBy: f.sortBy,
        polymarketTags: f.polymarketTags,
        eventCategories: f.eventCategories,
        minCapital: f.minCapitalText !== '' ? Number(f.minCapitalText) : null,
        minTraders: f.minTradersText !== '' ? Number(f.minTradersText) : null,
        hasSignal: f.hasSignal,
        includeTraderScores: f.includeTraderScores,  // ← passed through
        limit: f.limit,
        maxDaysRunning: f.maxAgeDaysText !== '' ? Number(f.maxAgeDaysText) : null,
        minEdgeDisparity: f.minEdgeDisparityText !== '' ? Number(f.minEdgeDisparityText) : null,
    };
}

// ---------------------------------------------------------------------------
// Internal shared styles
// ---------------------------------------------------------------------------

const labelSx = {
    fontSize: '9px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'text.secondary',
} as const;

const selectSx = {
    fontSize: '12px',
    '& .MuiSelect-select': { py: 0.625 },
};

// Clickable tag/category chip
function FilterChip({
    tag,
    active,
    onClick,
}: {
    tag: string;
    active?: boolean;
    onClick?: (tag: string) => void;
}) {
    return (
        <Box
            component="span"
            onClick={onClick ? e => { (e as React.MouseEvent).stopPropagation(); onClick(tag); } : undefined}
            sx={{
                display: 'inline-block',
                fontSize: '9.5px',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                color: active ? 'primary.main' : 'text.secondary',
                border: 1,
                borderColor: active ? 'primary.main' : 'divider',
                bgcolor: active ? (t: any) => `${t.palette.primary.main}14` : 'transparent',
                borderRadius: 0.75,
                px: 0.625,
                py: '1px',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.12s',
                '&:hover': onClick ? { borderColor: 'primary.main' } : undefined,
            }}
        >
            {tag.replace(/-/g, ' ')}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// MarketFilters component
// ---------------------------------------------------------------------------

interface MarketFiltersProps {
    groups: string[];
    value: MarketFilterValues;
    onChange: (patch: Partial<MarketFilterValues>) => void;
    onReset: () => void;
    resultCount: number | null;
    loading?: boolean;
}

export function MarketFilters({
    groups,
    value,
    onChange,
    onReset,
    resultCount,
    loading,
}: MarketFiltersProps) {
    // Metric groups for dropdown
    const metricGroups = useMemo(() => {
        const order: MetricDef['group'][] = ['Capital', 'Traders', 'Edge'];
        return order.map(g => ({
            group: g,
            items: CONVICTION_METRICS.filter(m => m.group === g),
        }));
    }, []);

    const hasActiveFilters =
        value.polymarketTags.length > 0 ||
        value.eventCategories.length > 0 ||
        value.minCapitalText !== '' ||
        value.minTradersText !== '' ||
        value.hasSignal ||
        value.minPriceText !== '' ||
        value.maxPriceText !== '' ||
        value.maxAgeDaysText !== DEFAULT_FILTERS.maxAgeDaysText ||
        value.minEdgeDisparityText !== DEFAULT_FILTERS.minEdgeDisparityText;

    const toggleTag = (t: string) =>
        onChange({
            polymarketTags: value.polymarketTags.includes(t)
                ? value.polymarketTags.filter(x => x !== t)
                : [...value.polymarketTags, t],
        });

    const toggleCategory = (c: string) =>
        onChange({
            eventCategories: value.eventCategories.includes(c)
                ? value.eventCategories.filter(x => x !== c)
                : [...value.eventCategories, c],
        });

    return (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {/* Row 1 — primary controls */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                {/* Group */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Group</Typography>
                    <Select
                        size="small"
                        value={value.group}
                        onChange={e => onChange({ group: e.target.value })}
                        sx={{ ...selectSx, minWidth: 130 }}
                    >
                        {groups.map(g => (
                            <MenuItem key={g} value={g} sx={{ fontSize: '12px' }}>{g}</MenuItem>
                        ))}
                    </Select>
                </Box>

                {/* Rank by */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Rank by</Typography>
                    <Select
                        size="small"
                        value={value.sortBy}
                        onChange={e => onChange({ sortBy: e.target.value as ConvictionMetric })}
                        sx={{ ...selectSx, minWidth: 160 }}
                    >
                        {metricGroups.flatMap(mg => [
                            <MenuItem
                                key={`h-${mg.group}`}
                                disabled
                                sx={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.6, lineHeight: 2 }}
                            >
                                {mg.group}
                            </MenuItem>,
                            ...mg.items.map(m => (
                                <MenuItem key={m.value} value={m.value} sx={{ fontSize: '12px' }}>
                                    {m.label}
                                </MenuItem>
                            )),
                        ])}
                    </Select>
                </Box>

                {/* Min capital */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Min capital ($)</Typography>
                    <TextField
                        size="small"
                        type="number"
                        placeholder="0"
                        value={value.minCapitalText}
                        onChange={e => onChange({ minCapitalText: e.target.value })}
                        sx={{ width: 110, '& input': { fontSize: '12px', py: 0.75 } }}
                    />
                </Box>

                {/* Min traders */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Min traders</Typography>
                    <TextField
                        size="small"
                        type="number"
                        placeholder="1"
                        value={value.minTradersText}
                        onChange={e => onChange({ minTradersText: e.target.value })}
                        sx={{ width: 90, '& input': { fontSize: '12px', py: 0.75 } }}
                    />
                </Box>

                {/* Min price */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Min price</Typography>
                    <TextField
                        size="small"
                        type="number"
                        placeholder="0.00"
                        value={value.minPriceText}
                        onChange={e => onChange({ minPriceText: e.target.value })}
                        sx={{ width: 80, '& input': { fontSize: '12px', py: 0.75 } }}
                    />
                </Box>

                {/* Max price */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Max price</Typography>
                    <TextField
                        size="small"
                        type="number"
                        placeholder="1.00"
                        value={value.maxPriceText}
                        onChange={e => onChange({ maxPriceText: e.target.value })}
                        sx={{ width: 80, '& input': { fontSize: '12px', py: 0.75 } }}
                    />
                </Box>

                {/* Max age (days) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Max age (days)</Typography>
                    <TextField
                        size="small"
                        type="number"
                        placeholder="7"
                        value={value.maxAgeDaysText}
                        onChange={e => onChange({ maxAgeDaysText: e.target.value })}
                        sx={{ width: 100, '& input': { fontSize: '12px', py: 0.75 } }}
                    />
                </Box>

                {/* Min edge disparity */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Min edge disp.</Typography>
                    <TextField
                        size="small"
                        type="number"
                        placeholder="0.0"
                        value={value.minEdgeDisparityText}
                        onChange={e => onChange({ minEdgeDisparityText: e.target.value })}
                        sx={{ width: 100, '& input': { fontSize: '12px', py: 0.75 } }}
                    />
                </Box>

                {/* Has signals */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Signals</Typography>
                    <ToggleButton
                        value="has_signal"
                        size="small"
                        selected={value.hasSignal}
                        onChange={() => onChange({ hasSignal: !value.hasSignal })}
                        sx={{ fontSize: '11px', textTransform: 'none', py: 0.5, px: 1, gap: 0.5 }}
                    >
                        <BoltIcon sx={{ fontSize: 13 }} />
                        Has signals
                    </ToggleButton>
                </Box>

                {/* Trader scores — NEW control wired to includeTraderScores */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Trader scores</Typography>
                    <Tooltip
                        title="Include per-trader breakdown so rows expand to show individual positions. Heavier payload — enable only when needed."
                        arrow
                    >
                        <Box
                            onClick={() => onChange({ includeTraderScores: !value.includeTraderScores })}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', userSelect: 'none', height: 32 }}
                        >
                            <Checkbox
                                size="small"
                                checked={value.includeTraderScores}
                                sx={{ p: 0.25, pointerEvents: 'none' }}
                            />
                            <Typography sx={{ fontSize: '11px' }}>Include</Typography>
                        </Box>
                    </Tooltip>
                </Box>

                {/* Limit */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography sx={labelSx}>Show</Typography>
                    <Select
                        size="small"
                        value={value.limit}
                        onChange={e => onChange({ limit: Number(e.target.value) })}
                        sx={{ ...selectSx, minWidth: 80 }}
                    >
                        {[50, 100, 200, 500].map(n => (
                            <MenuItem key={n} value={n} sx={{ fontSize: '12px' }}>{n}</MenuItem>
                        ))}
                    </Select>
                </Box>

                {/* Result count + reset */}
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {loading && (
                        <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>Loading…</Typography>
                    )}
                    {resultCount != null && !loading && (
                        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                            {resultCount} market{resultCount === 1 ? '' : 's'}
                        </Typography>
                    )}
                    {hasActiveFilters && (
                        <Typography
                            onClick={onReset}
                            sx={{ fontSize: '11px', color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                            Clear filters
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Row 2 — category chips */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ ...labelSx, mr: 0.25 }}>Categories</Typography>
                {VALID_EVENT_CATEGORIES.map(c => (
                    <FilterChip
                        key={c}
                        tag={c}
                        active={value.eventCategories.includes(c)}
                        onClick={toggleCategory}
                    />
                ))}
            </Box>

            {/* Row 3 — polymarket tag chips */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ ...labelSx, mr: 0.25 }}>Tags</Typography>
                {VALID_POLYMARKET_TAGS.map(t => (
                    <FilterChip
                        key={t}
                        tag={t}
                        active={value.polymarketTags.includes(t)}
                        onClick={toggleTag}
                    />
                ))}
            </Box>
        </Box>
    );
}